import { useEffect, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { isAuMobile, maskPhone, toE164 } from '@l2u/shared/auPhone';
import { AuthSplitLayout } from '../components/AuthSplitLayout.tsx';
import { Btn } from '../components/Btn.tsx';
import { CodeInput } from './CodeInput.tsx';
import {
  cleanupUnverified,
  enrollPhone,
  enrollTotp,
  resendCooldownRemaining,
  sendSmsChallenge,
  verifyEnroll,
  verifyPhone,
  type EnrollResult,
} from '../lib/mfa.ts';

// One-time two-factor setup, shown before a vendor can reach any portal page.
//
// This screen talks ONLY to Supabase — never to the Worker. That is what lets
// companyAuth demand aal2 with no exception for the not-yet-enrolled (spec
// §3.3/D6): a vendor sitting here has made no /api/me call to carve out.
//
// The copy leads with WHY. A mandatory extra step with no reason reads as a
// portal fault and generates support tickets; naming FedEx as the requirement
// and saying it is one-time answers both questions a vendor has.
//
// `smsEnabled` is per-environment (D12). SMS is only offered where the Send
// SMS hook and the Supabase Phone factor both exist — otherwise a vendor picks
// the option that never texts them.
//
// No phone pre-fill, deliberately: reading the vendor's contact_phone would
// need /api/me, and that call is exactly what this screen must not make. The
// number is typed. contact_phone is frequently a landline anyway.

type Flow =
  | { kind: 'choose' }
  | { kind: 'totp'; setup: EnrollResult }
  | { kind: 'phone-number'; phone: string }
  | { kind: 'phone-code'; phone: string; factorId: string; challengeId: string };

/**
 * A method that cannot even be STARTED is a configuration fault, not something
 * the vendor did. GoTrue's raw refusal ("Unsupported phone provider", say)
 * reads as gibberish, so name the situation and point at the way out — which
 * is why the "choose a different method" link has to exist alongside it.
 */
function startFailure(method: string, e: unknown): string {
  const detail = e instanceof Error ? e.message : String(e);
  return `Setting up ${method} is not available right now. Try the other method, or contact support. (${detail})`;
}

export function MfaEnrolScreen({
  client,
  smsEnabled,
  supportEmail,
  onEnrolled,
  onSignOut,
}: {
  client: SupabaseClient;
  smsEnabled: boolean;
  supportEmail: string;
  onEnrolled: () => void;
  onSignOut: () => void;
}) {
  const [flow, setFlow] = useState<Flow>({ kind: 'choose' });
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sentAt, setSentAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // Guards the auto-verify: onChange fires again while the request is in
  // flight, and a second challengeAndVerify with the same code fails.
  const submitting = useRef(false);

  const cooldown = resendCooldownRemaining(sentAt, now);

  // Ticks only while a cooldown is actually running, so the screen is not
  // re-rendering once a second for its whole life.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setNow(Date.now()), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function startTotp() {
    setErr(null);
    setBusy(true);
    try {
      // An abandoned earlier attempt leaves an unverified factor whose
      // friendlyName collides with this one, which is what makes a retry fail.
      await cleanupUnverified(client);
      setFlow({ kind: 'totp', setup: await enrollTotp(client) });
      setCode('');
    } catch (e) {
      setErr(startFailure('authenticator app', e));
    } finally {
      setBusy(false);
    }
  }

  async function sendCode(phone: string) {
    const e164 = toE164(phone);
    if (!e164) {
      setErr('Enter a valid Australian mobile (04xx xxx xxx).');
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      await cleanupUnverified(client);
      const { factorId } = await enrollPhone(client, e164);
      const challengeId = await sendSmsChallenge(client, factorId);
      setFlow({ kind: 'phone-code', phone, factorId, challengeId });
      setCode('');
      setSentAt(Date.now());
      setNow(Date.now());
    } catch (e) {
      setErr(startFailure('text message', e));
    } finally {
      setBusy(false);
    }
  }

  // Resend re-challenges the SAME factor. The new challengeId replaces the old
  // one: a code verified against a stale challenge fails even when the digits
  // are right.
  async function resend() {
    if (flow.kind !== 'phone-code' || cooldown > 0 || busy) return;
    setErr(null);
    setBusy(true);
    try {
      const challengeId = await sendSmsChallenge(client, flow.factorId);
      setFlow({ ...flow, challengeId });
      setCode('');
      setSentAt(Date.now());
      setNow(Date.now());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function verify(value: string) {
    if (value.length !== 6 || submitting.current) return;
    if (flow.kind !== 'totp' && flow.kind !== 'phone-code') return;
    submitting.current = true;
    setErr(null);
    setBusy(true);
    try {
      if (flow.kind === 'totp') await verifyEnroll(client, flow.setup.factorId, value);
      else await verifyPhone(client, flow.factorId, flow.challengeId, value);
      onEnrolled();
    } catch (e) {
      setErr((e as Error).message);
      // Clear it: the field is maxLength 6 and already full, so leaving the
      // rejected digits there forces the vendor to backspace six times before
      // they can retry — and leaves the button live to resubmit the same
      // wrong code.
      setCode('');
    } finally {
      setBusy(false);
      submitting.current = false;
    }
  }

  function onCode(next: string) {
    setCode(next);
    if (next.length < 6) setErr(null);
    if (next.length === 6) void verify(next); // auto-verify; the button is the fallback
  }

  // Back to the method choice, from ANY state.
  //
  // Without this a vendor is stranded by two ordinary mistakes: mistyping the
  // mobile (the code goes to a stranger and the code step only offers Resend
  // to that same wrong number), or picking SMS in an environment where the
  // Phone factor is off (a raw GoTrue refusal and no other option). The only
  // other exit is Sign out, which costs them a fresh emailed link and the wait
  // for it — for a typo.
  function backToChoose() {
    setFlow({ kind: 'choose' });
    setCode('');
    setErr(null);
    setSentAt(null);
  }

  const chooseAnother = flow.kind !== 'choose' && (
    <button
      type="button"
      onClick={backToChoose}
      disabled={busy}
      className="self-start text-sm font-semibold text-slate-500 hover:underline disabled:opacity-50"
    >
      {flow.kind === 'phone-code'
        ? 'Use a different number or method'
        : 'Choose a different method'}
    </button>
  );

  return (
    <AuthSplitLayout
      variant="vendor"
      headline="Set up two-factor authentication"
      subtext="A one-time setup that keeps your compliance records yours."
    >
      <div className="flex flex-col gap-4">
        <h2 className="text-2xl text-slate-900">Two-factor authentication</h2>

        {flow.kind === 'choose' && (
          <>
            <p className="text-sm leading-relaxed text-slate-500">
              FedEx requires a second check on every vendor account, so you will need to set this up
              before you can open the portal. You only do this once — after that it is a 6-digit
              code at sign-in.
            </p>
            <p className="text-sm leading-relaxed text-slate-500">
              Use Google Authenticator, Microsoft Authenticator, or any app you already have.
            </p>
            <Btn onClick={() => void startTotp()} loading={busy} className="w-full">
              Use an authenticator app
            </Btn>
            {smsEnabled && (
              <Btn
                variant="secondary"
                onClick={() => {
                  setErr(null);
                  setFlow({ kind: 'phone-number', phone: '' });
                }}
                disabled={busy}
                className="w-full"
              >
                Use a text message (SMS)
              </Btn>
            )}
          </>
        )}

        {flow.kind === 'totp' && (
          <>
            <p className="text-sm text-slate-500">1. Scan this code with your authenticator app:</p>
            <img
              src={flow.setup.qrCode}
              alt="Two-factor setup QR code"
              className="mx-auto h-44 w-44"
            />
            <p className="text-center text-xs text-slate-500">
              Cannot scan? Enter this key instead:
              <br />
              <span className="font-mono text-sm tracking-wider text-slate-700">
                {flow.setup.secret}
              </span>
            </p>
            <p className="text-sm text-slate-500">2. Enter the 6-digit code it shows:</p>
            <CodeInput value={code} onChange={onCode} disabled={busy} autoFocus />
            <Btn
              onClick={() => void verify(code)}
              loading={busy}
              disabled={code.length < 6}
              className="w-full"
            >
              Verify and continue
            </Btn>
          </>
        )}

        {flow.kind === 'phone-number' && (
          <>
            <p className="text-sm leading-relaxed text-slate-500">
              We will text a 6-digit code to your mobile. Australian mobile numbers only — a
              landline cannot receive it.
            </p>
            <input
              className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-primary"
              inputMode="tel"
              autoComplete="tel"
              value={flow.phone}
              onChange={(e) => setFlow({ kind: 'phone-number', phone: e.target.value })}
              placeholder="04xx xxx xxx"
              aria-label="Mobile number"
              autoFocus
            />
            <Btn
              onClick={() => void sendCode(flow.phone)}
              loading={busy}
              disabled={!isAuMobile(flow.phone)}
              className="w-full"
            >
              Send code
            </Btn>
          </>
        )}

        {flow.kind === 'phone-code' && (
          <>
            <p className="text-sm text-slate-500">
              Enter the 6-digit code we texted to <strong>{maskPhone(flow.phone)}</strong>.
            </p>
            <CodeInput value={code} onChange={onCode} disabled={busy} autoFocus />
            <Btn
              onClick={() => void verify(code)}
              loading={busy}
              disabled={code.length < 6}
              className="w-full"
            >
              Verify and continue
            </Btn>
            <button
              type="button"
              onClick={() => void resend()}
              disabled={busy || cooldown > 0}
              className="self-start text-sm font-semibold text-brand-primary disabled:opacity-50"
            >
              {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
            </button>
          </>
        )}

        {chooseAnother}

        {err && (
          // role="alert" so a screen reader announces a rejected code; the
          // auto-verify means there is no submit event to hear otherwise.
          <p role="alert" className="text-sm text-red-600">
            {err}
          </p>
        )}

        <div className="pt-1 text-sm text-slate-500">
          Need help? Contact{' '}
          <a href={`mailto:${supportEmail}`} className="font-semibold text-brand-primary">
            {supportEmail}
          </a>
          .
        </div>
        <button
          type="button"
          onClick={onSignOut}
          className="self-start text-sm font-semibold text-slate-500 hover:underline"
        >
          Sign out
        </button>
      </div>
    </AuthSplitLayout>
  );
}
